import { useNavigate, useParams } from 'react-router-dom';

import {
  AnchorDocType,
  DocumentInconsistenciesBlock as DocumentInconsistenciesBlockType,
} from '../../../../../../../../../../../backend/src/types/types';
import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetKimThreadFile } from '@/api/queries/useGetKimThreadFile';
import { Icon } from '@/new/components/icon';
import { Skeleton } from '@/new/components/shadcn/skeleton';

interface KimDocumentInconsistenciesBlockProps {
  block: DocumentInconsistenciesBlockType;
}

export function KimDocumentInconsistenciesBlock({ block }: KimDocumentInconsistenciesBlockProps) {
  const { matterId, subsetId, threadId } = useParams<{
    matterId: string;
    subsetId: string;
    threadId: string;
  }>();
  const navigate = useNavigate();

  const isMatterFile = block.anchorDocType === AnchorDocType.MatterFile;

  const { data: existingDoc, isLoading: isLoadingExistingDoc } = useGetDoc(
    isMatterFile ? matterId : undefined,
    isMatterFile ? block.anchorDocId : null,
    subsetId,
  );

  const threadFileQuery = useGetKimThreadFile({
    matterId,
    subsetId,
    threadId,
    fileId: !isMatterFile ? block.anchorDocId : null,
  });

  const anchorDocName = isMatterFile ? (existingDoc?.file?.name ?? null) : (threadFileQuery.data?.name ?? null);
  const isLoadingAnchorDoc = isMatterFile ? isLoadingExistingDoc : threadFileQuery.isLoading;

  const inconsistencies = block.content ?? [];

  if (inconsistencies.length === 0) return null;

  const handleOpenViewer = () => {
    if (!matterId || !subsetId || !threadId) return;
    navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/${threadId}/verify/${block.id}`);
  };

  const inconsistencyLabel = `${inconsistencies.length} inconsistenc${inconsistencies.length === 1 ? 'y' : 'ies'}`;

  return (
    <div className="mx-auto my-4 max-w-3xl px-1 font-sans">
      <button
        type="button"
        onClick={handleOpenViewer}
        aria-label={anchorDocName ? `Open verification viewer for ${anchorDocName}` : 'Open verification viewer'}
        className="group relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white px-6 py-4.5 pb-4.25 text-left transition-colors duration-150 hover:border-gray-300 hover:shadow-lg/1 focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        <div className="min-w-0 flex-1">
          {isLoadingAnchorDoc ? (
            <Skeleton className="h-4 w-48" />
          ) : (
            <p className="max-w-4/5 truncate text-sm font-medium text-gray-800">{anchorDocName ?? 'Anchor document'}</p>
          )}
          <div className="mt-0.75 flex items-center gap-1.5">
            <p className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
              <Icon className="lucide--shield-alert text-red-600" />
              <span className="">{inconsistencyLabel}</span>
            </p>
            <p className="inline-flex items-center gap-0.75 text-[11px] text-gray-700 duration-150 group-hover:gap-1">
              View alongside document <Icon className="lucide--chevron-right" />
            </p>
          </div>
        </div>

        <VerificationViewerThumbnail />
      </button>
    </div>
  );
}

function VerificationViewerThumbnail() {
  return (
    <div className="absolute right-8 -bottom-2 flex shrink-0 flex-col items-center gap-1 duration-200 group-hover:-bottom-1.5">
      <div
        aria-hidden
        className="flex h-15 w-22 overflow-hidden rounded-md border border-gray-200 bg-gray-50 transition-colors duration-200 group-hover:border-red-300 group-hover:bg-red-50"
      >
        <div className="m-1.5 mb-0 grow space-y-0.75 rounded-sm border border-gray-100 bg-white p-1.5 pt-2 duration-200 group-hover:border-red-200">
          <div className="h-1 w-3/4 rounded-xs bg-gray-100 duration-200 group-hover:bg-red-100"></div>
          <div className="h-1 w-full rounded-xs bg-gray-100 duration-200 group-hover:bg-red-100"></div>
          <div className="h-1 w-1/2 rounded-xs bg-gray-100 duration-200 group-hover:bg-red-100"></div>
          <div className="h-1 w-full rounded-xs bg-gray-100 duration-200 group-hover:bg-red-100"></div>
        </div>
        <div className="w-[38%] space-y-1 border-l bg-white p-1.25 pt-2 duration-200 group-hover:border-red-300">
          <div className="h-1 w-full rounded-xs border-red-100 bg-gray-50 duration-200 group-hover:border-red-200 group-hover:bg-red-200"></div>
          <div className="h-1 w-full rounded-xs border-red-100 bg-gray-50 duration-200 group-hover:border-red-200 group-hover:bg-red-200"></div>
        </div>
      </div>
    </div>
  );
}
